/**
 * CLARTEXT · Fuehrerscheinkontrolle · Block E — Client-seitige Bildqualitaets-
 * Heuristik (Betreiberantwort Frage 3: P1, client-side, heuristisch, kostenfrei).
 *
 * Zweck: OFFENSICHTLICH unscharfe Fotos vor dem Netzwerk-Roundtrip erkennen
 * und den Mitarbeiter zu einem neuen Foto ermutigen. **Nicht blockierend** —
 * BE-004: der Foto-Submit darf durch diese Pruefung niemals scheitern. Bei
 * Erkennungsfehlern (Browser ohne Canvas, CORS-Probleme etc.) verhaelt sich
 * die Funktion neutral und gibt `{ ok: true }` zurueck.
 *
 * Verfahren:
 *   1. Bild in ein `HTMLCanvasElement` zeichnen (auf 512px Kantenlaenge
 *      herunterskaliert — schnelle Auswertung ohne Speicher-Peak).
 *   2. Graustufen-Konvertierung.
 *   3. 3x3 Laplace-Kernel anwenden.
 *   4. Varianz der Laplace-Antwort berechnen.
 *   5. Varianz < THRESHOLD_BLURRY  -> unscharf.
 *      Varianz > THRESHOLD_OK      -> ok.
 *      dazwischen                  -> ok (kein Fehlalarm bei Grenzfaellen).
 *
 * Schwelle konservativ gewaehlt (Laplace-Varianz < 60 gilt in der Literatur
 * als sehr unscharf; wir setzen 25 an, um nur die deutlich schlechten Faelle
 * zu markieren).
 */

const TARGET_DIM = 512;
const THRESHOLD_BLURRY = 25;

export const IMAGE_QUALITY_OK = "ok";
export const IMAGE_QUALITY_BLURRY = "blurry";
export const IMAGE_QUALITY_UNCHECKED = "unchecked";

/**
 * Prueft die Bildschaerfe eines File-Objekts.
 * @param {File} file
 * @returns {Promise<{quality: string, variance: number, note?: string}>}
 */
export async function assessImageQuality(file) {
  if (!file || typeof window === "undefined") {
    return { quality: IMAGE_QUALITY_UNCHECKED, variance: 0 };
  }
  try {
    const bitmap = await decodeImage(file);
    if (!bitmap) {
      return { quality: IMAGE_QUALITY_UNCHECKED, variance: 0 };
    }
    const variance = computeLaplaceVariance(bitmap);
    if (variance > 0 && variance < THRESHOLD_BLURRY) {
      return {
        quality: IMAGE_QUALITY_BLURRY,
        variance,
        note: "Foto wirkt unscharf. Bitte erneut versuchen (Handy ruhig halten, gute Beleuchtung).",
      };
    }
    return { quality: IMAGE_QUALITY_OK, variance };
  } catch (_e) {
    return { quality: IMAGE_QUALITY_UNCHECKED, variance: 0 };
  }
}

async function decodeImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch (_e) {
      // fall through zum HTMLImageElement-Pfad (Safari mit HEIC etc.)
    }
  }
  return await new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function computeLaplaceVariance(source) {
  const canvas = document.createElement("canvas");
  const w = source.width || source.naturalWidth || 0;
  const h = source.height || source.naturalHeight || 0;
  if (!w || !h) return 0;
  const scale = Math.min(TARGET_DIM / Math.max(w, h), 1);
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return 0;
  ctx.drawImage(source, 0, 0, cw, ch);
  const data = ctx.getImageData(0, 0, cw, ch).data;
  const gray = new Float32Array(cw * ch);
  for (let i = 0; i < gray.length; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    // Rec. 601 Luma-Konvertierung
    gray[i] = 0.299 * r + 0.587 * g + 0.114 * b;
  }
  const laplace = new Float32Array(cw * ch);
  let sum = 0;
  let count = 0;
  for (let y = 1; y < ch - 1; y++) {
    for (let x = 1; x < cw - 1; x++) {
      const idx = y * cw + x;
      const value =
        -4 * gray[idx] +
        gray[idx - 1] +
        gray[idx + 1] +
        gray[idx - cw] +
        gray[idx + cw];
      laplace[idx] = value;
      sum += value;
      count++;
    }
  }
  if (!count) return 0;
  const mean = sum / count;
  let variance = 0;
  for (let y = 1; y < ch - 1; y++) {
    for (let x = 1; x < cw - 1; x++) {
      const idx = y * cw + x;
      const d = laplace[idx] - mean;
      variance += d * d;
    }
  }
  return variance / count;
}
